import type { SDKLogger } from '../logging'
import type { SdkAttributes } from './sdk-attributes/get-core-sdk-attributes'

export interface RegisterToolProperties {
  name: string
  description: string
  inputJsonSchema: Record<string, unknown> | null
  outputJsonSchema: Record<string, unknown> | null
  responseFormat: string
}

interface ToolRegistryOptions {
  apiBaseUrl: string
  getAccessToken: () => Promise<string>
  logger?: SDKLogger
}

function buildRegisterToolsEndpoint(apiBaseUrl: string): string {
  return new URL('/tool-registry/register-tools', apiBaseUrl).toString()
}

export type ToolRegistryItem = {
  id?: string
  name?: string
  versionId?: string
}

function parseRegisterToolsResponse(
  responseJson: unknown,
): Record<string, ToolRegistryItem> {
  if (!Array.isArray(responseJson)) {
    throw new TypeError('registerTools response must be an array')
  }

  const toolNameToId: Record<string, ToolRegistryItem> = {}
  for (const [index, item] of responseJson.entries()) {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) {
      throw new TypeError(
        `registerTools response item at index ${index} must be an object`,
      )
    }

    const { id: toolId, name: toolName } = item as ToolRegistryItem
    if (typeof toolId !== 'string' || toolId.length === 0) {
      throw new TypeError(
        `registerTools response item at index ${index} is missing a valid id`,
      )
    }
    if (typeof toolName !== 'string' || toolName.length === 0) {
      throw new TypeError(
        `registerTools response item at index ${index} is missing a valid name`,
      )
    }

    toolNameToId[toolName] = item
  }

  return toolNameToId
}

export class ToolRegistry {
  public initialized: Promise<true> = Promise.resolve(true)

  private apiBaseUrl: string
  private getAccessToken: () => Promise<string>
  private logger?: SDKLogger
  private toolNameToId = new Map<string, ToolRegistryItem>()

  constructor({ apiBaseUrl, getAccessToken, logger }: ToolRegistryOptions) {
    this.apiBaseUrl = apiBaseUrl
    this.getAccessToken = getAccessToken
    this.logger = logger
  }

  getRegisteredTool(toolName: string): ToolRegistryItem | null {
    return this.toolNameToId.get(toolName) ?? null
  }

  registerTools({
    tools,
    sdkAttributes,
  }: {
    tools: Iterable<RegisterToolProperties>
    sdkAttributes: SdkAttributes | null
  }): void {
    const toolsList = [...tools]
    if (toolsList.length === 0 && !sdkAttributes) {
      this.initialized = Promise.resolve(true)
      return
    }

    this.initialized = this.executeRegisterTools({
      tools: toolsList,
      sdkAttributes: sdkAttributes,
    })
    void this.initialized
  }

  private async executeRegisterTools({
    tools,
    sdkAttributes,
  }: {
    tools: RegisterToolProperties[]
    sdkAttributes: SdkAttributes | null
  }): Promise<true> {
    try {
      const accessToken = await this.getAccessToken()
      const response = await fetch(
        buildRegisterToolsEndpoint(this.apiBaseUrl),
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            properties: tools.map((property) => ({
              name: property.name,
              description: property.description,
              inputJsonSchema: property.inputJsonSchema,
              outputJsonSchema: property.outputJsonSchema,
              responseFormat: property.responseFormat,
            })),
            sdk: sdkAttributes,
          }),
        },
      )

      if (!response.ok) {
        this.logger?.error?.(
          `Failed to register tools: HTTP ${response.status} ${await response.text()}`,
        )
        return true
      }

      const responseJson = await response.json()
      const registerToolsResponse = parseRegisterToolsResponse(responseJson)
      for (const [toolName, tool] of Object.entries(registerToolsResponse)) {
        this.toolNameToId.set(toolName, tool)
      }
    } catch (error) {
      this.logger?.error?.(
        `Failed to register tools: ${error instanceof Error ? error.message : String(error)}`,
      )
    }

    return true
  }
}
